import { useCallback, useEffect, useState } from 'react';
import { useActiveOrgId, usePeople } from './api';

/**
 * Whose timesheet are we looking at? Selection persists in localStorage per
 * org and is shared between TimePage and the topbar TimerWidget via a custom
 * event (localStorage `storage` events do not fire in the same tab).
 */

const EVENT = 'financials-time-person-changed';

function storageKey(orgId: string): string {
  return `financials-time-person:${orgId}`;
}

function readStored(orgId: string | undefined): string | null {
  if (!orgId) return null;
  try {
    return window.localStorage.getItem(storageKey(orgId));
  } catch {
    return null;
  }
}

export function usePersonSelection(): {
  /** Selected person id, or null until people load (backend resolves default). */
  personId: string | null;
  setPersonId: (id: string) => void;
} {
  const orgId = useActiveOrgId();
  const { data } = usePeople();
  const [stored, setStored] = useState<string | null>(() => readStored(orgId));

  useEffect(() => {
    setStored(readStored(orgId));
    const onChange = () => setStored(readStored(orgId));
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, [orgId]);

  const setPersonId = useCallback(
    (id: string) => {
      if (!orgId) return;
      try {
        window.localStorage.setItem(storageKey(orgId), id);
      } catch {
        /* private mode — selection just won't persist */
      }
      window.dispatchEvent(new Event(EVENT));
    },
    [orgId],
  );

  // Stored selection wins if it still exists; otherwise the backend default
  // (email match, else first active staff member).
  const people = data?.people;
  const valid = stored && people?.some((p) => p.id === stored) ? stored : null;
  const personId = valid ?? data?.defaultPersonId ?? null;

  return { personId, setPersonId };
}
